<template>
  <MtxPage width="sm">
    <template #header>
      <h1>Finish Account Setup</h1>
    </template>

    <MtxCard>
      <h3>Welcome to Matryx!</h3>
      <p>
        Please enter in your name and email in order to use our platform. Your email will be used
        for notifications such as when a submission of yours wins a tournament. None of this
        information will be available to other users.
      </p>
      <form @submit.prevent>
        <div class="grid grid-2-columns grid-gap">
          <MtxInput
            v-model="localUser.firstName"
            label="First Name"
            :validation="$v.localUser.firstName"
            class="mb-2"
          />

          <MtxInput
            v-model="localUser.lastName"
            label="Last Name"
            :validation="$v.localUser.lastName"
            class="mb-2"
          />
        </div>

        <MtxInput
          v-model="localUser.dob"
          label="Date of Birth"
          :validation="$v.localUser.dob"
          :errors="{
            validDate: 'Invalid date format, try yyyy-mm-dd.',
            minDate: 'You\'re not that old...',
            maxDate: 'You must be at least 13 to use Matryx.'
          }"
          class="mb-2"
        />

        <MtxInput
          v-model="localUser.email"
          label="Email"
          :validation="$v.localUser.email"
          class="mb-3"
        />

        <div class="text-center">
          <button :class="{ 'btn--disabled': $v.$invalid || isLoading }" @click="onSubmit">
            Submit
          </button>
        </div>
      </form>
    </MtxCard>
  </MtxPage>
</template>

<script>
import { mapActions, mapState } from "vuex"
import { email, maxLength, minLength, name, required } from "@/utils/validators"

export default {
  data: () => ({
    isLoading: false,
    localUser: {}
  }),

  computed: {
    ...mapState(["user"])
  },

  created() {
    if (!this.user.ethAddress) {
      this.$router.replace("/")
    }

    this.localUser = { ...this.user }
  },

  beforeRouteLeave(to, from, next) {
    if (!this.user.email) {
      this.logout()
    }

    next()
  },

  methods: {
    ...mapActions(["editUser", "logout"]),

    async onSubmit() {
      this.$v.$touch()
      if (this.$v.$invalid || this.isLoading) return

      this.isLoading = true

      const dob = new Date(this.localUser.dob)
      const y = `${dob.getFullYear()}`
      const m = `${dob.getMonth() + 1}`.padStart(2, "0")
      const d = `${dob.getDate()}`.padStart(2, "0")
      this.localUser.dob = `${y}-${m}-${d}`

      const { success } = await this.editUser(this.localUser)
      this.isLoading = false

      if (success) {
        this.$router.back()
      }
    }
  },

  validations() {
    return {
      localUser: {
        firstName: {
          minLength: minLength(2),
          maxLength: maxLength(50),
          name,
          required
        },
        lastName: {
          minLength: minLength(2),
          maxLength: maxLength(50),
          name,
          required
        },
        dob: {
          validDate: date => !!+new Date(date),
          maxDate: date => {
            if (!this.$v.localUser.dob.validDate) return true

            const maxDate = new Date()
            maxDate.setFullYear(maxDate.getFullYear() - 13)
            return new Date(date) < maxDate
          },
          minDate: date => {
            if (!this.$v.localUser.dob.validDate) return true
            return new Date(date).getFullYear() >= 1900
          },
          required
        },
        email: {
          email,
          required
        }
      }
    }
  }
}
</script>
